import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';

import { settings } from '../settings';

/**
 * Asistente conversacional del Centro de Ayuda (solo backoffice).
 *
 * ⚠️ POR QUÉ ES UN API SLICE PROPIO Y NO CUELGA DE NINGUNO EXISTENTE:
 * el endpoint es un Lambda Function URL en OTRO ORIGEN y sin `Authorization`. Todos los
 * demás slices arman `baseUrl` con `settings.url.host` y varios inyectan el Bearer y
 * reintentan el refresh ante un 401 — nada de eso aplica acá, y meterlo en uno de ellos
 * mandaría el JWT de SIMP a un host de terceros.
 *
 * El `timeout` propio también es motivo suficiente: la consulta es SÍNCRONA y puede
 * tardar decenas de segundos, mientras que el resto de la app espera respuestas rápidas.
 */

/** La consulta es síncrona y lenta; el default de `fetch` (sin corte) deja la UI colgada. */
export const TIMEOUT_ASISTENTE_IA_MS = 60_000;

export interface ConsultaAsistenteArgs {
    /** Texto que escribió el usuario. */
    query: string;
    /** Hilo de la conversación. Lo genera el front y vive mientras dure la sesión. */
    sessionId: string;
    /** `usuario` del usuario logueado (no el id numérico): es lo que espera el backend. */
    userId: string;
}

/**
 * La respuesta del agente, verificada contra un payload real.
 *
 * Trae mucho más que el texto —`metrics`, `traces`, conteo de tokens—, nada de lo cual
 * usa la UI. Se tipa solo lo que se lee.
 */
export interface ConsultaAsistenteRawResponse {
    /** El texto de la respuesta. Es el campo canónico. */
    message?: string;
    /** Mismo contenido que `message` en los payloads observados; se usa como respaldo. */
    output?: string;
    metadata?: {
        session_id?: string;
        user_id?: string;
    };
}

export interface ConsultaAsistenteResponse {
    respuesta: string;
}

/**
 * Un shape inesperado tiene que romper acá y no pintar `undefined` dentro de una burbuja:
 * el usuario vería una respuesta en blanco sin entender que falló.
 *
 * `output` es respaldo y no la fuente principal: en el payload observado repite palabra
 * por palabra lo de `message`, pero por el nombre parece la salida cruda del agente, así
 * que es más probable que cambie de forma si mañana el agente usa herramientas.
 */
export const extraerRespuesta = (payload: ConsultaAsistenteRawResponse): string => {
    const texto = payload?.message ?? payload?.output;

    if (typeof texto !== 'string' || !texto.trim()) {
        throw new Error('El asistente respondió con un formato inesperado.');
    }

    return texto;
};

/**
 * El request completo, como función pura.
 *
 * Los tres headers se arman acá y no en `prepareHeaders` para que el contrato HTTP se
 * pueda verificar sin red de por medio — mismo criterio que
 * `construirFormDataCargaMasivaRar` en presentacionesRarApi. `Content-Type` lo pone
 * `fetchBaseQuery` solo, porque el body es un objeto.
 *
 * La URL es cadena vacía a propósito: el Function URL no tiene path, todo cuelga de la
 * raíz del `baseUrl`.
 */
export const construirRequestConsulta = ({ query, sessionId, userId }: ConsultaAsistenteArgs) => ({
    url: '',
    method: 'POST',
    headers: {
        'x-session-id': sessionId,
        'x-user-id': userId,
        profile: settings.asistenteIA.profile
    },
    body: { query }
});

export const asistenteIAApi = createApi({
    reducerPath: 'asistenteIAApi',
    baseQuery: fetchBaseQuery({
        baseUrl: settings.url.asistenteIA,
        timeout: TIMEOUT_ASISTENTE_IA_MS
    }),
    endpoints: (builder) => ({
        consultarAsistenteIA: builder.mutation<ConsultaAsistenteResponse, ConsultaAsistenteArgs>({
            query: construirRequestConsulta,
            transformResponse: (raw: ConsultaAsistenteRawResponse) => ({
                respuesta: extraerRespuesta(raw)
            })
        })
    })
});

export const { useConsultarAsistenteIAMutation } = asistenteIAApi;
